/**
 * Category Discount Management Component
 * Modern UI with design system components
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Edit2, Trash2, X } from 'lucide-react';
import categoryDiscountService from '../services/categoryDiscountService';
import categoryService from '../services/categoryService';
import { showSuccess, showError } from '../services/toastService';
import './CategoryDiscountManagement.css';

// UI Components
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';

const CategoryDiscountManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [discounts, setDiscounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(() => ['add', 'edit'].includes(searchParams.get('action')));
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [filterStatus, setFilterStatus] = useState(() => searchParams.get('status') || 'all');
  const [searchCategory, setSearchCategory] = useState(() => searchParams.get('search') || '');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteDiscountId, setDeleteDiscountId] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    category_id: '',
    discount_percent: '',
    start_date: '',
    end_date: '',
    description: ''
  });

  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (filterStatus !== 'all') next.set('status', filterStatus); else next.delete('status');
      if (searchCategory) next.set('search', searchCategory); else next.delete('search');
      return next;
    }, { replace: true });
  }, [filterStatus, searchCategory, setSearchParams]);

  useEffect(() => {
    fetchDiscounts();
    fetchCategories();
  }, [filterStatus]);

  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'edit' && !editingDiscount && discounts.length) {
      const id = Number(searchParams.get('id'));
      const discount = discounts.find((item) => item.id === id);
      if (discount) handleEdit(discount);
    }
  }, [discounts]);

  const fetchDiscounts = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus !== 'all') {
        params.status = filterStatus;
      }
      const response = await categoryDiscountService.getAllDiscounts(params);
      setDiscounts(response.data.discounts);
    } catch (error) {
      console.error('Error fetching discounts:', error);
      alert(error.message || 'Failed to fetch discounts');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categoryService.getAllCategories();
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.category_id) {
      newErrors.category_id = 'Category is required';
    }

    if (!formData.discount_percent) {
      newErrors.discount_percent = 'Discount percentage is required';
    } else if (formData.discount_percent < 0 || formData.discount_percent > 100) {
      newErrors.discount_percent = 'Discount must be between 0 and 100';
    }

    if (!formData.start_date) {
      newErrors.start_date = 'Start date is required';
    }

    if (!formData.end_date) {
      newErrors.end_date = 'End date is required';
    } else if (new Date(formData.end_date) <= new Date(formData.start_date)) {
      newErrors.end_date = 'End date must be after start date';
    }

    setValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showError('Please fix the validation errors before submitting');
      return;
    }

    setLoading(true);
    try {
      if (editingDiscount) {
        await categoryDiscountService.updateDiscount(editingDiscount.id, formData);
        showSuccess('Discount updated successfully');
      } else {
        await categoryDiscountService.createDiscount(formData);
        showSuccess('Discount created successfully');
      }
      resetForm();
      fetchDiscounts();
    } catch (error) {
      console.error('Error saving discount:', error);
      if (error.errors) {
        const newErrors = {};
        error.errors.forEach(err => {
          newErrors[err.field] = err.message;
        });
        setValidationErrors(newErrors);
      }
      showError(error.message || 'Failed to save discount');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (discount) => {
    setEditingDiscount(discount);
    setFormData({
      category_id: discount.category_id.toString(),
      discount_percent: discount.discount_percent,
      start_date: discount.start_date.split('T')[0],
      end_date: discount.end_date.split('T')[0],
      description: discount.description || ''
    });
    setShowForm(true);
    setValidationErrors({});
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set('action', 'edit');
      next.set('id', String(discount.id));
      return next;
    }, { replace: true });
  };

  const handleDeleteConfirm = async () => {
    setLoading(true);
    try {
      await categoryDiscountService.deleteDiscount(deleteDiscountId);
      showSuccess('Discount deleted successfully');
      fetchDiscounts();
    } catch (error) {
      console.error('Error deleting discount:', error);
      showError(error.message || 'Failed to delete discount');
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setDeleteDiscountId(null);
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteDiscountId(id);
    setDeleteDialogOpen(true);
  };

  const handleToggle = async (id) => {
    setLoading(true);
    try {
      await categoryDiscountService.toggleDiscount(id);
      fetchDiscounts();
    } catch (error) {
      console.error('Error toggling discount:', error);
      alert(error.message || 'Failed to toggle discount');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingDiscount(null);
    setFormData({ category_id: '', discount_percent: '', start_date: '', end_date: '', description: '' });
    setValidationErrors({});
    setShowForm(true);
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set('action', 'add');
      next.delete('id');
      return next;
    }, { replace: true });
  };

  const resetForm = () => {
    setFormData({
      category_id: '',
      discount_percent: '',
      start_date: '',
      end_date: '',
      description: ''
    });
    setEditingDiscount(null);
    setShowForm(false);
    setValidationErrors({});
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete('action');
      next.delete('id');
      return next;
    }, { replace: true });
  };

  const getStatusVariant = (status) => {
    const variants = {
      active: 'active',
      upcoming: 'pending',
      expired: 'destructive',
      inactive: 'inactive'
    };
    return variants[status] || 'inactive';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const filteredDiscounts = discounts.filter(discount => {
    if (searchCategory) {
      return discount.category?.name.toLowerCase().includes(searchCategory.toLowerCase());
    }
    return true;
  });



  return (
    <div className="discount-management">
      {/* Header */}
      <div className="discount-header">
        <h2>Category Discounts</h2>
        <Button onClick={handleAdd}>
          <Plus size={14} /> Add Discount
        </Button>
      </div>

      {/* Add/edit uses a modal so the list remains in context. */}
      {showForm && (
        <div className="modal-overlay-new" onClick={resetForm}>
          <div className="modal-content-new discount-form-modal" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header-new">
              <h2 className="modal-title-new">{editingDiscount ? 'Edit Discount' : 'Add Discount'}</h2>
              <button type="button" className="modal-close-new" onClick={resetForm} aria-label="Close"><X size={16} /></button>
            </div>
            <div className="modal-body-new">
            <form onSubmit={handleSubmit} className="discount-form">
              {/* Category Selection */}
              <div className="discount-form-group">
                <Label htmlFor="category_id">
                  Category <span className="discount-required">*</span>
                </Label>
                <Select
                  value={formData.category_id}
                  onValueChange={(value) => handleSelectChange('category_id', value)}
                >
                  <SelectTrigger className={validationErrors.category_id ? 'discount-input-error' : ''}>
                    <SelectValue placeholder="Select Category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.length === 0 ? (
                      <SelectItem value="no-categories" disabled>
                        No categories available
                      </SelectItem>
                    ) : (
                      categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id.toString()}>
                          {cat.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {validationErrors.category_id && (
                  <span className="discount-error-message">{validationErrors.category_id}</span>
                )}
              </div>

              <div className="discount-form-grid">
                <div className="discount-form-group discount-percent-field">
                  <Label htmlFor="discount_percent">
                    Discount Percentage <span className="discount-required">*</span>
                  </Label>
                  <Input
                    id="discount_percent"
                    name="discount_percent"
                    type="number"
                    value={formData.discount_percent}
                    onChange={handleInputChange}
                    placeholder="e.g., 10"
                    min="0"
                    max="100"
                    className={validationErrors.discount_percent ? 'discount-input-error' : ''}
                  />
                  {validationErrors.discount_percent && <span className="discount-error-message">{validationErrors.discount_percent}</span>}
                </div>

                <div className="discount-form-group">
                  <Label htmlFor="start_date">Start Date <span className="discount-required">*</span></Label>
                  <Input id="start_date" name="start_date" type="date" value={formData.start_date} onChange={handleInputChange}
                    className={validationErrors.start_date ? 'discount-input-error' : ''} />
                  {validationErrors.start_date && <span className="discount-error-message">{validationErrors.start_date}</span>}
                </div>

                <div className="discount-form-group">
                  <Label htmlFor="end_date">End Date <span className="discount-required">*</span></Label>
                  <Input id="end_date" name="end_date" type="date" value={formData.end_date} onChange={handleInputChange}
                    className={validationErrors.end_date ? 'discount-input-error' : ''} />
                  {validationErrors.end_date && <span className="discount-error-message">{validationErrors.end_date}</span>}
                </div>
              </div>

              <div className="discount-form-group">
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="e.g., Festival sale or bulk buyer offer"
                />
              </div>

              {/* Form Actions */}
              <div className="discount-form-actions">
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
                <Button type="submit" disabled={loading} className="discount-submit-btn">
                  {loading ? 'Saving…' : editingDiscount ? 'Update Discount' : 'Create Discount'}
                </Button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}

      {/* List remains visible behind the add/edit dialog. */}
      <Card className="discount-table-card">
          {/* Table Header with Filter */}
          <div className="discount-table-header">
            <h3 className="discount-table-title">Discounts List</h3>
            <div className="discount-filter-inline">
              <Label>Filter by Status:</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="discount-filter-select-inline">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="upcoming">Upcoming</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                </SelectContent>
              </Select>
              <Input
                placeholder="Search category..."
                value={searchCategory}
                onChange={(e) => setSearchCategory(e.target.value)}
                className="discount-search-input"
              />
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Category</TableHead>
                <TableHead>Discount</TableHead>
                <TableHead>Start Date</TableHead>
                <TableHead>End Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Active</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && filteredDiscounts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan="7" className="discount-table-center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : filteredDiscounts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan="7" className="discount-table-center">
                    No discounts found
                  </TableCell>
                </TableRow>
              ) : (
                filteredDiscounts.map(discount => (
                  <TableRow key={discount.id}>
                    <TableCell>
                      <div>
                        <div style={{ fontWeight: 600 }}>{discount.category?.name}</div>
                        {discount.description && (
                          <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
                            {discount.description}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span style={{ fontSize: '16px', fontWeight: 600, color: '#3b82f6' }}>
                        {discount.discount_percent}%
                      </span>
                    </TableCell>
                    <TableCell>{formatDate(discount.start_date)}</TableCell>
                    <TableCell>{formatDate(discount.end_date)}</TableCell>
                    <TableCell>
                      <Badge variant={getStatusVariant(discount.status)}>
                        {discount.status.toUpperCase()}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <label className="pm-toggle" title={discount.is_active ? 'Deactivate' : 'Activate'}>
                        <input
                          type="checkbox"
                          checked={discount.is_active}
                          onChange={() => handleToggle(discount.id)}
                          disabled={loading}
                        />
                        <span className="pm-toggle-slider"></span>
                      </label>
                    </TableCell>
                    <TableCell>
                      <div className="discount-action-buttons">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(discount)}
                          className="discount-edit-btn"
                        >
                          <Edit2 size={16} />
                        </Button>
                        <AlertDialog open={deleteDialogOpen && deleteDiscountId === discount.id}>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteClick(discount.id)}
                              className="discount-delete-btn"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent style={{
                            maxWidth: '400px',
                            borderRadius: '12px',
                            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
                          }}>
                            <AlertDialogHeader style={{ paddingBottom: '16px' }}>
                              <AlertDialogTitle style={{
                                fontSize: '20px',
                                fontWeight: '700',
                                color: '#111827',
                                marginBottom: '8px'
                              }}>
                                Delete Discount
                              </AlertDialogTitle>
                              <AlertDialogDescription style={{
                                fontSize: '14px',
                                color: '#6b7280',
                                lineHeight: '1.5'
                              }}>
                                Are you sure you want to delete the discount for "{discount.category?.name}"? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <div style={{
                              display: 'flex',
                              gap: '12px',
                              justifyContent: 'flex-end',
                              paddingTop: '16px',
                              borderTop: '1px solid #e5e7eb'
                            }}>
                              <AlertDialogCancel onClick={() => setDeleteDialogOpen(false)} style={{
                                padding: '8px 16px',
                                borderRadius: '6px',
                                border: '1px solid #d1d5db',
                                backgroundColor: '#ffffff',
                                color: '#111827',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '500',
                                transition: 'all 0.2s ease'
                              }}>
                                Cancel
                              </AlertDialogCancel>
                              <AlertDialogAction onClick={handleDeleteConfirm} style={{
                                padding: '8px 16px',
                                borderRadius: '6px',
                                border: 'none',
                                backgroundColor: '#dc2626',
                                color: '#ffffff',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '500',
                                transition: 'all 0.2s ease'
                              }}>
                                Delete
                              </AlertDialogAction>
                            </div>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
    </div>
  );
};

export default CategoryDiscountManagement;
